/**
 * Excel Service
 * Handles reading and processing data from Excel worksheets
 */

import { config } from "../utils/config";
import { ExcelRowData, UploadPreview, ValidationError, MappingInput } from "../types";

/* global Excel */

class ExcelService {
  /**
   * Find the column index by checking common header names
   */
  private findColumnIndex(
    headers: string[],
    possibleNames: string[]
  ): number {
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]?.toString().toLowerCase().trim();
      if (possibleNames.includes(header)) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Read data from the current worksheet selection or used range
   */
  async readWorksheetData(): Promise<UploadPreview> {
    const result: UploadPreview = {
      rows: [],
      totalRows: 0,
      validRows: 0,
      errorRows: 0,
      errors: [],
    };

    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRange();
      usedRange.load("values");

      await context.sync();

      const values = usedRange.values as (string | number | boolean)[][];

      if (!values || values.length < 2) {
        result.errors.push({
          row: 0,
          column: "",
          message: "No data found. Please ensure your worksheet has headers and data rows.",
        });
        return;
      }

      // First row is headers
      const headers = values[0].map((h) => h?.toString().toLowerCase().trim() || "");

      // Find column indices
      const customerCodeIndex = this.findColumnIndex(headers, config.excelColumns.customerCode);
      const segmentNameIndex = this.findColumnIndex(headers, config.excelColumns.segmentName);
      const notesIndex = this.findColumnIndex(headers, config.excelColumns.notes);

      // Validate required columns exist
      if (customerCodeIndex === -1) {
        result.errors.push({
          row: 1,
          column: "customer_code",
          message: `Required column 'customer_code' not found. Expected one of: ${config.excelColumns.customerCode.join(", ")}`,
        });
      }

      if (segmentNameIndex === -1) {
        result.errors.push({
          row: 1,
          column: "segment_name",
          message: `Required column 'segment_name' not found. Expected one of: ${config.excelColumns.segmentName.join(", ")}`,
        });
      }

      if (customerCodeIndex === -1 || segmentNameIndex === -1) {
        result.errorRows = values.length - 1;
        result.totalRows = values.length - 1;
        return;
      }

      // Process data rows (skip header)
      for (let i = 1; i < values.length; i++) {
        const row = values[i];
        const rowNumber = i + 1; // Excel row number (1-indexed)

        const customerCode = row[customerCodeIndex]?.toString().trim() || "";
        const segmentName = row[segmentNameIndex]?.toString().trim() || "";
        const notes = notesIndex !== -1 ? row[notesIndex]?.toString().trim() || "" : "";

        result.totalRows++;

        // Validate row
        const rowErrors: ValidationError[] = [];

        if (!customerCode) {
          rowErrors.push({
            row: rowNumber,
            column: "customer_code",
            message: "Customer code is required",
          });
        } else if (customerCode.length > 100) {
          rowErrors.push({
            row: rowNumber,
            column: "customer_code",
            message: "Customer code must be 100 characters or less",
          });
        }

        if (!segmentName) {
          rowErrors.push({
            row: rowNumber,
            column: "segment_name",
            message: "Segment name is required",
          });
        }

        if (rowErrors.length > 0) {
          result.errors.push(...rowErrors);
          result.errorRows++;
        } else {
          result.validRows++;
          result.rows.push({
            customerCode,
            segmentName,
            notes: notes || undefined,
            rowNumber,
          });
        }
      }
    });

    return result;
  }

  /**
   * Convert preview data to mapping input format for API
   */
  convertToMappingInput(rows: ExcelRowData[]): MappingInput[] {
    return rows.map((row) => ({
      customerCode: row.customerCode,
      segmentName: row.segmentName,
      notes: row.notes,
    }));
  }

  /**
   * Convert preview data to CSV format
   */
  convertToCsv(rows: ExcelRowData[]): string {
    const headers = ["customer_code", "segment_name", "notes"];
    const csvRows = [headers.join(",")];

    for (const row of rows) {
      const values = [
        this.escapeCsvValue(row.customerCode),
        this.escapeCsvValue(row.segmentName),
        this.escapeCsvValue(row.notes || ""),
      ];
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  }

  /**
   * Escape special characters in CSV values
   */
  private escapeCsvValue(value: string): string {
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  /**
   * Create a CSV Blob from the data
   */
  createCsvBlob(rows: ExcelRowData[]): Blob {
    const csv = this.convertToCsv(rows);
    return new Blob([csv], { type: "text/csv" });
  }

  /**
   * Highlight error cells in the worksheet
   */
  async highlightErrors(errors: ValidationError[]): Promise<void> {
    if (errors.length === 0) return;

    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      // Group errors by row for efficiency
      const errorRows = new Set(errors.map((e) => e.row));

      for (const rowNumber of errorRows) {
        // Highlight the entire row with a light red background
        const range = sheet.getRange(`${rowNumber}:${rowNumber}`);
        range.format.fill.color = "#FFCDD2"; // Light red
      }

      await context.sync();
    });
  }

  /**
   * Clear error highlighting from the worksheet
   */
  async clearHighlights(): Promise<void> {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRange();
      usedRange.format.fill.clear();
      await context.sync();
    });
  }

  /**
   * Get the selected range information
   */
  async getSelectionInfo(): Promise<{ address: string; rowCount: number; columnCount: number }> {
    let info = { address: "", rowCount: 0, columnCount: 0 };

    await Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load(["address", "rowCount", "columnCount"]);
      await context.sync();

      info = {
        address: range.address,
        rowCount: range.rowCount,
        columnCount: range.columnCount,
      };
    });

    return info;
  }
}

export const excel = new ExcelService();
export default excel;
